/**
 * FacultyFormPage - Admin form for creating/editing faculty members
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getAdminFacultyMember,
  createFaculty,
  updateAdminFaculty,
  uploadFacultyHeadshot,
  removeFacultyHeadshot,
  getShows,
  DuplicateFacultyError,
  type Show,
} from '../../../api/admin';

interface FacultyFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  bio: string;
  default_rate_cents: number;
  show_ids: string[];
  send_invitation: boolean;
}

export default function FacultyFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [shows, setShows] = useState<Show[]>([]);
  const [showsLoading, setShowsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateFaculty, setDuplicateFaculty] = useState<{ facultyId: string; hasAccount: boolean } | null>(null);

  const [formData, setFormData] = useState<FacultyFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    bio: '',
    default_rate_cents: 0,
    show_ids: [],
    send_invitation: true,
  });

  // Separate state for pay rate input to avoid cursor jumping
  const [payRateInput, setPayRateInput] = useState('');

  // Headshot upload state (edit mode only — on create the faculty row doesn't
  // exist yet, so the upload endpoint has no target).
  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [headshotPreview, setHeadshotPreview] = useState<string | null>(null);
  const [headshotError, setHeadshotError] = useState<string | null>(null);
  // Tracks the server-side headshot URL separately from the preview, so we
  // know whether there's something to DELETE when the admin clicks Remove
  // (vs just clearing a locally-staged-but-unsaved file selection).
  const [originalHeadshotUrl, setOriginalHeadshotUrl] = useState<string | null>(null);
  const [isRemovingHeadshot, setIsRemovingHeadshot] = useState(false);

  // State for showing invitation link after creation
  const [createdFaculty, setCreatedFaculty] = useState<{
    id: string;
    invitation_url?: string;
    email_sent?: boolean;
  } | null>(null);

  useEffect(() => {
    loadShows();
    if (id) {
      loadFaculty(id);
    }
  }, [id]);

  const loadShows = async () => {
    try {
      setShowsLoading(true);
      const showsData = await getShows();
      setShows(showsData);
    } catch (err) {
      console.error('Failed to load shows:', err);
    } finally {
      setShowsLoading(false);
    }
  };

  const loadFaculty = async (facultyId: string) => {
    try {
      setIsLoading(true);
      const faculty = await getAdminFacultyMember(facultyId);
      setFormData({
        first_name: faculty.first_name || '',
        last_name: faculty.last_name || '',
        email: faculty.email || '',
        phone: faculty.phone || '',
        bio: faculty.bio || '',
        default_rate_cents: faculty.default_rate_cents || 0,
        show_ids: faculty.shows?.map((s) => s.id) || [],
        send_invitation: false,
      });
      // Set pay rate input for editing
      if (faculty.default_rate_cents) {
        setPayRateInput(String(faculty.default_rate_cents / 100));
      }
      // Seed headshot preview + remember the original server-side URL so the
      // Remove button knows whether there's something to DELETE.
      setHeadshotPreview(faculty.headshot_url || null);
      setOriginalHeadshotUrl(faculty.headshot_url || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load faculty');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Separate handler for pay rate to avoid cursor jumping
  const handlePayRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and one decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setPayRateInput(value);
      // Convert to cents for form data
      const numValue = parseFloat(value || '0');
      const cents = Math.round(numValue * 100);
      setFormData((prev) => ({ ...prev, default_rate_cents: cents }));
    }
  };

  const handleHeadshotFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHeadshotError(null);
    const file = e.target.files?.[0] || null;
    if (!file) {
      setHeadshotFile(null);
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setHeadshotError('Invalid file type. Use JPEG, PNG, or WebP.');
      return;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setHeadshotError('File too large. Maximum size is 5MB.');
      return;
    }
    setHeadshotFile(file);
    setHeadshotPreview(URL.createObjectURL(file));
  };

  const handleCancelHeadshotUpload = () => {
    setHeadshotFile(null);
    setHeadshotError(null);
    // Revert preview to the saved server-side image (or empty if none).
    setHeadshotPreview(originalHeadshotUrl);
  };

  const handleRemoveHeadshot = async () => {
    if (!id) return;
    if (!window.confirm("Remove this faculty's headshot? This will permanently delete the saved image.")) {
      return;
    }
    try {
      setIsRemovingHeadshot(true);
      setHeadshotError(null);
      await removeFacultyHeadshot(id);
      setHeadshotFile(null);
      setHeadshotPreview(null);
      setOriginalHeadshotUrl(null);
    } catch (err) {
      setHeadshotError(
        err instanceof Error ? err.message : 'Failed to remove headshot',
      );
    } finally {
      setIsRemovingHeadshot(false);
    }
  };

  const handleShowToggle = (showId: string) => {
    setFormData((prev) => ({
      ...prev,
      show_ids: prev.show_ids.includes(showId)
        ? prev.show_ids.filter((id) => id !== showId)
        : [...prev.show_ids, showId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDuplicateFaculty(null);

    // Validation
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!isEditing && formData.default_rate_cents <= 0) {
      setError('Pay rate is required and must be greater than $0');
      return;
    }
    if (!isEditing && formData.show_ids.length === 0) {
      setError('At least one show certification is required');
      return;
    }

    try {
      setIsSaving(true);

      if (isEditing) {
        // Two-phase save: headshot first (multipart), then the other fields
        // (JSON). If the upload fails, abort so we don't half-save.
        if (headshotFile && id) {
          try {
            await uploadFacultyHeadshot(id, headshotFile);
          } catch (uploadErr) {
            setError(
              uploadErr instanceof Error
                ? `Failed to upload headshot: ${uploadErr.message}. Other fields not saved.`
                : 'Failed to upload headshot. Other fields not saved.',
            );
            setIsSaving(false);
            return;
          }
        }
        await updateAdminFaculty(id, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone || null,
          bio: formData.bio || null,
          default_rate_cents: formData.default_rate_cents || null,
          show_ids: formData.show_ids,
        });
        navigate(`/admin/faculty/${id}`);
      } else {
        const newFaculty = await createFaculty({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          default_rate_cents: formData.default_rate_cents,
          show_ids: formData.show_ids,
        });
        // Show invitation link instead of immediately navigating
        setCreatedFaculty({
          id: newFaculty.id,
          invitation_url: newFaculty.invitation_url,
          email_sent: newFaculty.email_sent,
        });
      }
    } catch (err) {
      if (err instanceof DuplicateFacultyError) {
        setError(err.message);
        setDuplicateFaculty({ facultyId: err.facultyId, hasAccount: err.hasAccount });
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save faculty');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-warm-200 rounded w-1/4 mb-8" />
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="space-y-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i}>
                <div className="h-4 bg-warm-100 rounded w-1/4 mb-2" />
                <div className="h-10 bg-warm-100 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Only offer active shows for certification, but keep any show that is
  // already selected visible (edit-mode: a faculty may be certified for a show
  // that later went inactive — the admin still needs to see/uncheck it).
  const visibleShows = shows.filter(
    (s) => s.is_active || formData.show_ids.includes(s.id)
  );

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link
          to={isEditing ? `/admin/faculty/${id}` : '/admin/faculty'}
          className="text-warm-600 hover:text-warm-900 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
          {isEditing ? 'Edit Faculty' : 'Invite Faculty'}
        </h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          <p>{error}</p>
          {duplicateFaculty && (
            <p className="mt-2">
              <Link to={`/admin/faculty/${duplicateFaculty.facultyId}`} className="underline font-medium">
                View existing profile
              </Link>
              {!duplicateFaculty.hasAccount && ' — Use Resend Invitation on their profile.'}
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          {/* Headshot — edit mode only */}
          {isEditing && (
            <div>
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Headshot</h2>
              <div className="flex items-start gap-4">
                {headshotPreview ? (
                  <img
                    src={headshotPreview}
                    alt="Headshot preview"
                    className="w-24 h-24 rounded-full object-cover border border-warm-200"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-warm-100 flex items-center justify-center text-warm-400 text-xs text-center px-1">
                    No headshot
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleHeadshotFileChange}
                    className="block text-sm text-warm-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border file:border-warm-200 file:bg-warm-50 file:text-warm-700 file:cursor-pointer hover:file:bg-warm-100"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {headshotFile && (
                      <button
                        type="button"
                        onClick={handleCancelHeadshotUpload}
                        disabled={isSaving || isRemovingHeadshot}
                        className="text-sm text-warm-600 hover:text-warm-800 underline disabled:opacity-50"
                      >
                        Cancel upload
                      </button>
                    )}
                    {!headshotFile && originalHeadshotUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveHeadshot}
                        disabled={isSaving || isRemovingHeadshot}
                        className="text-sm text-danger-600 hover:text-danger-800 underline disabled:opacity-50"
                      >
                        {isRemovingHeadshot ? 'Removing…' : 'Remove headshot'}
                      </button>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-warm-500">
                    JPEG, PNG, or WebP. Max 5MB. Uploads when you save.
                  </p>
                  {headshotError && (
                    <p className="mt-2 text-sm text-danger-700">{headshotError}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-semibold text-warm-900 mb-4">
              Basic Information
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-1 sm:col-span-1">
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Jane"
                />
              </div>

              <div className="col-span-1 sm:col-span-1">
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Smith"
                />
              </div>

              <div className="col-span-1 sm:col-span-1">
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="jane@example.com"
                />
              </div>

              <div className="col-span-1 sm:col-span-1">
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Pay Rate (per class) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    name="default_rate"
                    value={payRateInput}
                    onChange={handlePayRateChange}
                    required
                    className="w-full pl-8 pr-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="100"
                  />
                </div>
                <p className="mt-1 text-xs text-warm-500">
                  Enter amount in dollars (e.g., 100 or 150.50)
                </p>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Brief biography and teaching experience..."
            />
            <p className="mt-1 text-sm text-warm-500">
              Faculty can edit this themselves during onboarding.
            </p>
          </div>

          {/* Show Certifications */}
          <div>
            <h2 className="text-lg font-semibold text-warm-900 mb-4">
              Show Certifications *
            </h2>
            <p className="text-sm text-warm-600 mb-4">
              Select at least one show this faculty member is certified to teach.
            </p>

            {showsLoading ? (
              // Skeleton while shows load — avoids flashing the "No shows
              // available" empty-state on a slow connection before the list
              // arrives.
              <div
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3"
                aria-busy="true"
                aria-label="Loading shows"
              >
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 border border-warm-200 rounded-lg animate-pulse"
                  >
                    <div className="w-4 h-4 bg-warm-200 rounded" />
                    <div className="h-4 bg-warm-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {visibleShows.map((show) => (
                    <label
                      key={show.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.show_ids.includes(show.id)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-warm-200 hover:bg-warm-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.show_ids.includes(show.id)}
                        onChange={() => handleShowToggle(show.id)}
                        className="w-4 h-4 text-primary-600 border-warm-300 rounded focus:ring-primary-500"
                      />
                      <div className="flex items-center gap-2">
                        {show.logo_url && (
                          <img
                            src={show.logo_url}
                            alt={show.name}
                            className="w-6 h-6 rounded object-cover"
                          />
                        )}
                        <span className="text-sm font-medium text-warm-900">
                          {show.name}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>

                {visibleShows.length === 0 && (
                  <p className="text-warm-500 text-sm">
                    No shows available. Add shows first in Tour Management.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Send Invitation */}
          {!isEditing && (
            <div className="border-t border-warm-100 pt-6">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="send_invitation"
                  checked={formData.send_invitation}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 text-primary-600 border-warm-300 rounded focus:ring-primary-500"
                />
                <div>
                  <span className="font-medium text-warm-900">
                    Send invitation email
                  </span>
                  <p className="text-sm text-warm-600 mt-1">
                    The faculty member will receive an email with instructions to complete their onboarding,
                    including setting up their profile and connecting Stripe for payments and tax documents.
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 mt-6">
          <Link
            to={isEditing ? `/admin/faculty/${id}` : '/admin/faculty'}
            className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary disabled:opacity-50"
          >
            {isSaving
              ? 'Saving...'
              : isEditing
              ? 'Save Changes'
              : 'Create & Send Invitation'}
          </button>
        </div>
      </form>

      {/* Invitation Preview */}
      {!isEditing && formData.send_invitation && (
        <div className="mt-8 bg-warm-50 rounded-xl p-6">
          <h3 className="font-semibold text-warm-900 mb-4">Invitation Email Preview</h3>
          <div className="bg-white rounded-lg p-4 border border-warm-200">
            <p className="text-sm text-warm-600 mb-2">
              <strong>To:</strong> {formData.email || '(email address)'}
            </p>
            <p className="text-sm text-warm-600 mb-4">
              <strong>Subject:</strong> You're invited to join Stage Door Access as a faculty member
            </p>
            <div className="text-sm text-warm-700 space-y-2">
              <p>Hi {formData.first_name || '(first name)'},</p>
              <p>
                You've been invited to join Stage Door Access as a faculty member!
                Stage Door Access connects Broadway talent with dance studios across the country
                for masterclass opportunities.
              </p>
              <p>
                To complete your profile and start receiving teaching assignments, please click
                the button below to begin your onboarding.
              </p>
              <div className="bg-primary-600 text-white text-center py-2 px-4 rounded-lg inline-block mt-2">
                Complete Your Profile
              </div>
              <p className="mt-4">
                During onboarding, you'll need to:
              </p>
              <ul className="list-disc list-inside ml-2">
                <li>Upload a professional headshot</li>
                <li>Enter professional dance bio</li>
                <li>Create/Connect Stripe for payments and tax documents</li>
              </ul>
              <p className="mt-4">
                If you have any questions, please reach out to our team.
              </p>
              <p className="mt-4">
                Best,<br />
                The Stage Door Access Team
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal - shows after faculty creation with invitation link */}
      {createdFaculty && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-display font-bold text-warm-900">
                Faculty Invited!
              </h2>
              <p className="text-warm-600 mt-2">
                {formData.first_name} {formData.last_name} has been added to the system.
              </p>
            </div>

            {/* Email status */}
            <div className={`p-4 rounded-lg mb-4 ${createdFaculty.email_sent ? 'bg-success-50' : 'bg-warning-50'}`}>
              {createdFaculty.email_sent ? (
                <p className="text-success-700 text-sm">
                  ✓ Invitation email sent to <strong>{formData.email}</strong>
                </p>
              ) : (
                <p className="text-warning-700 text-sm">
                  ⚠ Email could not be sent. Please share the invitation link manually.
                </p>
              )}
            </div>

            {/* Invitation link */}
            {createdFaculty.invitation_url && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-warm-700 mb-2">
                  Invitation Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={createdFaculty.invitation_url}
                    className="flex-1 px-3 py-2 bg-warm-50 border border-warm-200 rounded-lg text-sm text-warm-700"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(createdFaculty.invitation_url || '');
                    }}
                    className="px-4 py-2 bg-warm-100 hover:bg-warm-200 text-warm-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Copy
                  </button>
                </div>
                <p className="mt-2 text-xs text-warm-500">
                  Share this link with the faculty member to complete their onboarding.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/admin/faculty')}
                className="flex-1 px-4 py-2 border border-warm-200 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
              >
                Back to Faculty List
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/faculty/${createdFaculty.id}`)}
                className="flex-1 btn-primary"
              >
                View Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
